// AppProviders — compose QueryClientProvider + AuthProvider + ThemeProvider +
// FocusZoneProvider + RouterProvider. Point d'entrée unique pour le mount.
// Source : PRD 09 §3 app providers.
import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "react-router";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { FocusZoneProvider } from "@/contexts/FocusZoneContext";
import { createQueryClient } from "@/lib/queryClient";
import { initPostHog } from "@/lib/posthog";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import "@/lib/i18n"; // Init i18next (side-effect import)
import { router } from "./router";

const queryClient = createQueryClient();

export function AppProviders() {
  useEffect(() => {
    initPostHog();
  }, []);

  // Provider order : Auth au-dessus de Theme — un changement de theme
  // (prefers-color-scheme live) ne re-render pas le sous-arbre auth.
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <FocusZoneProvider>
              <RouterProvider router={router} />
              <Toaster position="top-right" richColors closeButton />
              {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
            </FocusZoneProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
